import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { matchStatus } from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/taxons-tree';
const HTTP_METHOD = 'get';

export type MakeListTaxonTreeDeps<TaxonMenu extends Schema.Top> = {
  readonly taxonMenu: TaxonMenu;
};

type ListTaxonTreeParams = {
  root_taxon_code: string;
  depth?: number;
};

export const makeListTaxonTree = <TaxonMenu extends Schema.Top>(
  deps: MakeListTaxonTreeDeps<TaxonMenu>
) => {
  const ListTaxonTreeError = createBackendApiError({
    domain: 'Taxon',
    endpoint: 'ListTree',
    path: ENDPOINT,
    identifierField: 'root_taxon_code',
  });

  const listTaxonTreeFetch = (params: ListTaxonTreeParams) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            root_taxon_code: params.root_taxon_code,
            ...(params.depth !== undefined ? { depth: params.depth } : {}),
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'http.request.query.root_taxon_code': params.root_taxon_code,
          'http.request.query.depth': params.depth ?? 'default',
        },
      })
    );

  const listTaxonTree = (params: ListTaxonTreeParams) =>
    listTaxonTreeFetch(params).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.taxonMenu))(response),
          404: (response) =>
            Effect.fail(
              new ListTaxonTreeError.NotFoundError({
                root_taxon_code: params.root_taxon_code,
                response,
              })
            ),
          orElse: (response) =>
            Effect.fail(
              new ListTaxonTreeError.ApiRequestError({
                root_taxon_code: params.root_taxon_code,
                response,
              })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.TaxonListTree,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} tree items for root taxon "${params.root_taxon_code}"`
        )
      ),
      Effect.withSpan('api.taxon.listTree', {
        kind: 'internal',
      })
    );

  return { listTaxonTree, ListTaxonTreeError };
};
